import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { authApi } from '../../../utils/authApi';

const OTPVerificationModal = ({ isOpen, onClose, initialChannel = 'email', initialDestination = '' , onVerified }) => {
  const [channel, setChannel] = useState(initialChannel);
  const [destination, setDestination] = useState(initialDestination || '');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [active, setActive] = useState(false);

  const keyFor = (c, d) => {
    const dest = (d || '').trim().toLowerCase();
    return `otp_state_${c}_${dest}`;
  };

  const loadPersisted = (c, d) => {
    try {
      const raw = localStorage?.getItem(keyFor(c, d));
      if (!raw) return null;
      const data = JSON.parse(raw);
      return data || null;
    } catch (_) { return null; }
  };

  const persistState = (c, d, state) => {
    try {
      localStorage?.setItem(keyFor(c, d), JSON.stringify(state));
    } catch (_) {}
  };

  const clearPersisted = (c, d) => {
    try {
      localStorage?.removeItem(keyFor(c, d));
    } catch (_) {}
  };

  const hydrateFromPersisted = (c, d) => {
    const saved = loadPersisted(c, d);
    if (!saved) {
      setCooldown(0);
      setExpiresIn(0);
      setActive(false);
      return;
    }
    const now = Date.now();
    const cdLeft = saved.cooldownUntil ? Math.max(0, Math.ceil((saved.cooldownUntil - now) / 1000)) : 0;
    const exLeft = saved.expiresAt ? Math.max(0, Math.ceil((saved.expiresAt - now) / 1000)) : 0;
    setCooldown(cdLeft);
    setExpiresIn(exLeft);
    setActive(exLeft > 0 || cdLeft > 0);
    if (exLeft > 0 && !success) setSuccess('Code sent');
  };

  useEffect(() => {
    if (isOpen) {
      setChannel(initialChannel);
      setDestination(initialDestination || '');
      setCode('');
      setError('');
      setSuccess('');
      setTimeout(() => hydrateFromPersisted(initialChannel, initialDestination || ''), 0);
    }
  }, [isOpen, initialChannel, initialDestination]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 1 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (!expiresIn) return;
    const t = setInterval(() => setExpiresIn((c) => (c > 1 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [expiresIn]);

  const canSend = useMemo(() => !sending && cooldown === 0 && !!channel && !active, [sending, cooldown, channel, active]);
  const canVerify = useMemo(() => !verifying && code.trim().length === 6 && !!channel, [verifying, code, channel]);

  const handleSend = async () => {
    setError('');
    setSuccess('');
    setSending(true);
    try {
      const token = localStorage?.getItem('auth_token');
      const { data, error } = await authApi.sendOTP({ token, channel, destination: destination || undefined });
      if (error) {
        if (error.status === 429 && typeof error.retry_after_seconds === 'number') {
          const left = Math.max(0, error.retry_after_seconds);
          setCooldown(left);
          persistState(channel, destination, { cooldownUntil: Date.now() + left * 1000, expiresAt: 0 });
        }
        setError(error.message || 'Failed to send code');
      } else {
        setSuccess('Code sent');
        if (typeof data?.expires_in_seconds === 'number') {
          const ex = data.expires_in_seconds;
          setExpiresIn(ex);
          setActive(true);
          persistState(channel, destination, { cooldownUntil: 0, expiresAt: Date.now() + ex * 1000 });
        }
      }
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    setSuccess('');
    setVerifying(true);
    try {
      const token = localStorage?.getItem('auth_token');
      const { data, error } = await authApi.verifyOTP({ token, channel, destination: destination || undefined, code: code.trim() });
      if (error) {
        setError(error.message || 'Invalid or expired code');
      } else {
        setSuccess('Verified');
        clearPersisted(channel, destination);
        if (onVerified) onVerified({ channel, destination });
        setTimeout(() => onClose?.(), 600);
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    hydrateFromPersisted(channel, destination);
  }, [channel, destination, isOpen]);

  useEffect(() => {
    if (expiresIn > 0) return;
    if (cooldown > 0) return;
    if (!active) return;
    setActive(false);
    clearPersisted(channel, destination);
  }, [expiresIn, cooldown]);

  useEffect(() => {
    if (cooldown > 0) return;
    if (expiresIn > 0) {
      setActive(true);
    } else if (active) {
      setActive(false);
    }
  }, [cooldown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-4 w-full max-w-md">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="ShieldCheck" size={18} />
            <h3 className="text-base font-semibold text-foreground">Verify {channel === 'phone' ? 'Phone' : 'Email'}</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {error ? (
            <div className="flex items-center space-x-2 text-destructive bg-destructive/10 p-2 rounded">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          ) : null}
          {success ? (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 dark:bg-green-900/20 p-2 rounded">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{success}</span>
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Channel</label>
            <div className="px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground">
              {channel === 'phone' ? 'Phone' : 'Email'}
            </div>
          </div>

          <Input
            label={channel === 'phone' ? 'Phone Number' : 'Email'}
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={channel === 'phone' ? '+123...' : 'user@example.com'}
            disabled={channel === 'email'}
            readOnly={channel === 'email'}
            className={channel === 'email' ? 'bg-muted cursor-not-allowed' : ''}
          />

          <div className="flex items-center space-x-2">
            <Button onClick={handleSend} disabled={!canSend} loading={sending} iconName="Send" iconPosition="left">
              {cooldown ? `Send in ${cooldown}s` : 'Send Code'}
            </Button>
            {expiresIn ? (
              <div className="text-xs text-muted-foreground">Expires in {expiresIn}s</div>
            ) : null}
            {active || cooldown ? (
              <div className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border border-primary/40 text-primary bg-primary/10">
                <Icon name="Clock" size={14} className="mr-1" />
                In progress
              </div>
            ) : null}
          </div>

          <Input
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
          />
        </div>

        <div className="p-4 border-t border-border flex items-center justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleVerify} disabled={!canVerify} loading={verifying} iconName="Check" iconPosition="left">Verify</Button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationModal;
